import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { getAuthHeader } from '../utils/auth';
import {
  Link2, ChevronRight, ChevronLeft, Check, Search,
  Users, Globe, FileText, Eye, Trash2, ToggleLeft, ToggleRight,
  RefreshCw, AlertTriangle, ExternalLink, Hash,
  CheckCircle2, Loader2, Pencil, X, ArrowRight
} from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
};

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-zinc-800/60 rounded-xl ${className}`} />
);

// ── Step indicator ────────────────────────────────────────────────────────────

const StepIndicator = ({ current, total, labels }) => (
  <div className="flex items-center gap-0 mb-6">
    {labels.map((label, i) => {
      const step = i + 1;
      const done = step < current;
      const active = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
              done ? 'bg-[#22c55e] border-[#22c55e] text-white'
                : active ? 'bg-[#6366f1]/20 border-[#6366f1] text-[#6366f1]'
                : 'bg-transparent border-zinc-700 text-zinc-600'
            }`}>
              {done ? <Check className="w-4 h-4" /> : step}
            </div>
            <span className={`text-[10px] font-semibold mt-1 whitespace-nowrap ${
              active ? 'text-white' : done ? 'text-[#22c55e]' : 'text-zinc-600'
            }`}>{label}</span>
          </div>
          {i < labels.length - 1 && (
            <div className={`flex-1 h-px mx-1 mb-4 ${done ? 'bg-[#22c55e]/60' : 'bg-zinc-800'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Preview table ─────────────────────────────────────────────────────────────

const LeadsPreviewTable = ({ leads, loading, error }) => {
  if (loading) return (
    <div className="space-y-2 mt-3">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-8" />)}
    </div>
  );
  if (error) return (
    <div className="flex items-center gap-2 mt-3 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
      <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
      <p className="text-xs text-red-300">{error}</p>
    </div>
  );
  if (!leads?.length) return (
    <p className="text-xs text-zinc-500 mt-3 text-center py-4">No leads found in this form yet</p>
  );
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-white/10">
            {['Name', 'Phone', 'Email', 'Date'].map(h => (
              <th key={h} className="px-3 py-2 text-left text-zinc-500 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={l.id || i} className="border-b border-white/5 last:border-0">
              <td className="px-3 py-2 text-white font-medium">{l.name}</td>
              <td className="px-3 py-2 text-zinc-400 font-mono">{l.phone}</td>
              <td className="px-3 py-2 text-zinc-500 truncate max-w-[120px]">{l.email}</td>
              <td className="px-3 py-2 text-zinc-600">{fmtDate(l.created_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Field Mapping Step ────────────────────────────────────────────────────────

const CRM_FIELD_OPTIONS = [
  { value: '', label: '— Skip this field —' },
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'company', label: 'Company' },
  { value: 'campaign_name', label: 'Campaign Name' },
  { value: 'ad_name', label: 'Ad Name' },
  { value: 'potential_value', label: 'Potential Value (₹)' },
  { value: 'notes', label: 'Notes' },
  { value: 'custom', label: '📌 Save as Custom Field' },
];

const FieldMappingStep = ({ metaFields, mappings, onChange, loading, error }) => {
  if (loading) return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14" />)}
    </div>
  );
  if (error) return (
    <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      <p className="text-xs text-amber-300">{error}</p>
    </div>
  );
  if (!metaFields.length) return (
    <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
      <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
      <p className="text-zinc-500 text-sm">No fields detected</p>
      <p className="text-zinc-600 text-xs mt-1">This form has no leads yet — default mapping will be used.</p>
    </div>
  );
  return (
    <div>
      <p className="text-sm text-zinc-400 mb-4">Map each Meta form field to a CRM field. Fields set to "Skip" won't be imported.</p>
      <div className="space-y-2">
        {metaFields.map(field => (
          <div key={field} className="flex items-center gap-3 bg-zinc-900/60 rounded-xl px-4 py-3 border border-white/5">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono text-zinc-300 truncate">{field}</p>
              <p className="text-[10px] text-zinc-600 mt-0.5">Meta field</p>
            </div>
            <ArrowRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
            <div className="flex-1">
              <select
                value={mappings[field] ?? ''}
                onChange={e => onChange(field, e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#6366f1]/50"
              >
                {CRM_FIELD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Active Links Table ────────────────────────────────────────────────────────

const ActiveLinksTable = ({ links, loading, onToggle, onDelete, onSync, onEditMapping, toggling, deleting, syncing }) => {
  if (loading) return (
    <div className="space-y-2">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  if (!links.length) return (
    <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl">
      <Link2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
      <p className="text-zinc-500 text-sm font-medium">No forms linked yet</p>
      <p className="text-zinc-600 text-xs mt-1">Use the wizard above to connect your first form.</p>
    </div>
  );

  return (
    <div className="overflow-x-auto rounded-2xl border border-white/5">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/5 bg-black/20">
            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Form</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden md:table-cell">Account / Page</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden lg:table-cell">Synced</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider hidden lg:table-cell">Last Sync</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {links.map(link => (
            <tr key={link.id} className="hover:bg-white/[0.02] transition-colors">
              <td className="px-4 py-3">
                <div>
                  <p className="font-semibold text-white text-sm">{link.client_name || 'Unknown'}</p>
                  {link.client_company && (
                    <p className="text-xs text-zinc-500">{link.client_company}</p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div>
                  <p className="text-white text-sm">{link.form_name || link.form_id}</p>
                  <p className="text-[10px] text-zinc-600 font-mono mt-0.5">{link.form_id}</p>
                </div>
              </td>
              <td className="px-4 py-3 hidden md:table-cell">
                <div>
                  <p className="text-zinc-300 text-xs">{link.account_name || link.account_id}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{link.page_name || link.page_id}</p>
                </div>
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <span className="font-mono text-xs text-zinc-300">{link.leads_synced}</span>
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <span className="text-xs text-zinc-500">{link.last_synced_at ? fmtDate(link.last_synced_at) : 'Never'}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2 justify-end">
                  {/* Status badge */}
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    link.is_active
                      ? 'bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/20'
                      : 'bg-zinc-700/40 text-zinc-500 border border-zinc-700/40'
                  }`}>
                    {link.is_active ? 'ACTIVE' : 'PAUSED'}
                  </span>
                  {/* Edit Field Mapping */}
                  <button
                    onClick={() => onEditMapping(link)}
                    title="Edit field mapping"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[#6366f1] hover:bg-[#6366f1]/10 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {/* Sync Now */}
                  <button
                    onClick={() => onSync(link.id, link.form_name || link.form_id)}
                    disabled={syncing[link.id]}
                    title="Sync leads now"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors disabled:opacity-40"
                  >
                    {syncing[link.id]
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <RefreshCw className="w-4 h-4" />
                    }
                  </button>
                  {/* Toggle */}
                  <button
                    onClick={() => onToggle(link.id)}
                    disabled={toggling[link.id]}
                    title={link.is_active ? 'Pause sync' : 'Resume sync'}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40"
                  >
                    {toggling[link.id]
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : link.is_active
                        ? <ToggleRight className="w-4 h-4 text-[#22c55e]" />
                        : <ToggleLeft className="w-4 h-4" />
                    }
                  </button>
                  {/* Delete */}
                  <button
                    onClick={() => onDelete(link.id, link.form_name || link.form_id)}
                    disabled={deleting[link.id]}
                    title="Remove link"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {deleting[link.id]
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />
                    }
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function MetaFormLinker() {
  const headers = { 'Content-Type': 'application/json', ...getAuthHeader() };

  // Wizard state
  const [step, setStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  // Field mapping wizard state
  const [wizardMetaFields, setWizardMetaFields] = useState([]);
  const [wizardMappings, setWizardMappings] = useState({});
  const [loadingFieldsWizard, setLoadingFieldsWizard] = useState(false);
  const [fieldsWizardError, setFieldsWizardError] = useState(null);

  // Edit mapping modal state
  const [editMappingLink, setEditMappingLink] = useState(null);
  const [editMappingFields, setEditMappingFields] = useState([]);
  const [editMappings, setEditMappings] = useState({});
  const [loadingEditFields, setLoadingEditFields] = useState(false);
  const [editFieldsError, setEditFieldsError] = useState(null);
  const [savingMapping, setSavingMapping] = useState(false);

  // Data
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [previewLeads, setPreviewLeads] = useState([]);
  const [links, setLinks] = useState([]);

  // Loading
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [linking, setLinking] = useState(false);

  // Errors
  const [formsError, setFormsError] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Search state for accounts, pages, clients and forms
  const [accountSearch, setAccountSearch] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [formSearch, setFormSearch] = useState('');

  // Table actions
  const [toggling, setToggling] = useState({});
  const [deleting, setDeleting] = useState({});
  const [syncing, setSyncing] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);

  // Fetch clients (CRM-enabled only)
  useEffect(() => {
    (async () => {
      setLoadingClients(true);
      try {
        const res = await fetch(`${API_URL}/admin/portal-access`, { headers: getAuthHeader() });
        if (res.ok) {
          const d = await res.json();
          setClients((d.clients || []).filter(c => c.portal_access?.crm_enabled && !c.portal_access?.is_paused));
        }
      } catch {
        toast.error('Failed to load clients');
      }
      setLoadingClients(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch links
  const fetchLinks = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        setLinks(d.links || []);
      }
    } catch {
      toast.error('Failed to load form links');
    }
    setLoadingLinks(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // Step 2: load accounts
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setSelectedAccount(null);
    setSelectedPage(null);
    setSelectedForm(null);
    setPreviewLeads([]);
    setStep(2);
    setLoadingAccounts(true);
    try {
      const res = await fetch(`${API_URL}/meta/lead-gen/accounts`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        setAccounts(d.accounts || []);
      } else {
        toast.error('Failed to load Meta accounts');
      }
    } catch {
      toast.error('Network error loading accounts');
    }
    setLoadingAccounts(false);
  };

  // Step 3: load pages
  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    setSelectedPage(null);
    setSelectedForm(null);
    setPreviewLeads([]);
    setPageSearch('');
    setStep(3);
    setLoadingPages(true);
    try {
      const res = await fetch(`${API_URL}/meta/lead-gen/pages?account_id=${account.id}`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        setPages(d.pages || []);
      } else {
        toast.error('Failed to load pages');
      }
    } catch {
      toast.error('Network error loading pages');
    }
    setLoadingPages(false);
  };

  // Step 4: load forms + preview
  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    setSelectedForm(null);
    setPreviewLeads([]);
    setFormsError(null);
    setStep(4);
    setLoadingForms(true);
    try {
      const res = await fetch(`${API_URL}/meta/lead-gen/forms?page_id=${page.id}`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        if (d.error) setFormsError(d.error);
        setForms(d.forms || []);
      } else {
        setFormsError('Could not load forms for this page');
      }
    } catch {
      setFormsError('Network error loading forms');
    }
    setLoadingForms(false);
  };

  const handleSelectForm = async (form) => {
    setSelectedForm(form);
    setPreviewLeads([]);
    setPreviewError(null);
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API_URL}/meta/lead-gen/forms/${form.id}/preview`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        if (d.error) setPreviewError(d.error);
        setPreviewLeads(d.leads || []);
      }
    } catch {
      setPreviewError('Could not load preview');
    }
    setLoadingPreview(false);
  };

  const handleContinueToMapping = async () => {
    if (!selectedForm || !selectedPage) return;
    setStep(5);
    setWizardMetaFields([]);
    setWizardMappings({});
    setFieldsWizardError(null);
    setLoadingFieldsWizard(true);
    try {
      const res = await fetch(
        `${API_URL}/meta/lead-gen/forms/${selectedForm.id}/fields?page_id=${selectedPage.id}`,
        { headers: getAuthHeader() }
      );
      if (res.ok) {
        const d = await res.json();
        if (d.error) setFieldsWizardError(d.error);
        setWizardMetaFields(d.meta_fields || []);
        setWizardMappings(d.auto_mappings || {});
      }
    } catch {
      setFieldsWizardError('Could not load form fields');
    }
    setLoadingFieldsWizard(false);
  };

  const handleLinkForm = async () => {
    if (!selectedClient || !selectedAccount || !selectedPage || !selectedForm) return;
    setLinking(true);
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          client_id: selectedClient.id,
          account_id: selectedAccount.id,
          account_name: selectedAccount.name,
          page_id: selectedPage.id,
          page_name: selectedPage.name,
          form_id: selectedForm.id,
          form_name: selectedForm.name,
        }),
      });
      const d = await res.json();
      if (res.ok) {
        // Save field mappings if we have any
        if (d.id && Object.keys(wizardMappings).length > 0) {
          try {
            await fetch(`${API_URL}/admin/crm/form-links/${d.id}/field-mapping`, {
              method: 'PUT',
              headers,
              body: JSON.stringify({ mappings: wizardMappings }),
            });
          } catch {
            // Non-fatal: mapping save failed, sync will use defaults
          }
        }
        toast.success(`Form "${selectedForm.name}" linked to ${selectedClient.name} — syncing leads…`);
        // Reset wizard
        setStep(1);
        setSelectedClient(null);
        setSelectedAccount(null);
        setSelectedPage(null);
        setSelectedForm(null);
        setPreviewLeads([]);
        setWizardMetaFields([]);
        setWizardMappings({});
        fetchLinks();
        // Auto-sync leads immediately so the client sees data right away
        if (d.id) {
          try {
            const syncRes = await fetch(`${API_URL}/admin/crm/form-links/${d.id}/sync`, {
              method: 'POST', headers,
            });
            const syncData = await syncRes.json();
            if (syncRes.ok && syncData.created > 0) {
              toast.success(`Synced ${syncData.created} leads to ${selectedClient?.name || 'client'} CRM`);
            }
          } catch {
            // Non-fatal — user can trigger manually via Sync Now
          }
        }
      } else {
        toast.error(d.detail || 'Failed to link form');
      }
    } catch {
      toast.error('Network error');
    }
    setLinking(false);
  };

  const handleToggle = async (linkId) => {
    setToggling(prev => ({ ...prev, [linkId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links/${linkId}/toggle`, {
        method: 'PATCH',
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const d = await res.json();
        setLinks(prev => prev.map(l => l.id === linkId ? { ...l, is_active: d.is_active } : l));
        toast.success(d.is_active ? 'Sync resumed' : 'Sync paused');
      } else {
        toast.error('Failed to toggle');
      }
    } catch {
      toast.error('Network error');
    }
    setToggling(prev => ({ ...prev, [linkId]: false }));
  };

  const handleDelete = async (linkId, formName) => {
    setDeleting(prev => ({ ...prev, [linkId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links/${linkId}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (res.ok) {
        setLinks(prev => prev.filter(l => l.id !== linkId));
        toast.success(`Removed link for "${formName}"`);
      } else {
        toast.error('Failed to delete link');
      }
    } catch {
      toast.error('Network error');
    }
    setDeleting(prev => ({ ...prev, [linkId]: false }));
  };

  const handleSync = async (linkId, formName) => {
    setSyncing(prev => ({ ...prev, [linkId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links/${linkId}/sync`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`Synced ${d.created} new leads from "${formName}"`);
        fetchLinks();
      } else {
        toast.error(d.error || 'Sync failed');
      }
    } catch {
      toast.error('Network error');
    }
    setSyncing(prev => ({ ...prev, [linkId]: false }));
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links/sync-all`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      const d = await res.json();
      toast.success(`Synced ${d.total_new_leads} new leads across ${d.synced} form(s)`);
      fetchLinks();
    } catch {
      toast.error('Sync all failed');
    }
    setSyncingAll(false);
  };

  const handleEditMapping = async (link) => {
    setEditMappingLink(link);
    setEditMappingFields([]);
    setEditMappings({});
    setEditFieldsError(null);
    setLoadingEditFields(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/crm/form-links/${link.id}/discovered-fields`,
        { headers: getAuthHeader() }
      );
      if (res.ok) {
        const d = await res.json();
        if (d.error) setEditFieldsError(d.error);
        setEditMappingFields(d.meta_fields || []);
        setEditMappings(d.existing_mappings || {});
      }
    } catch {
      setEditFieldsError('Could not load form fields');
    }
    setLoadingEditFields(false);
  };

  const handleSaveEditMapping = async () => {
    if (!editMappingLink) return;
    setSavingMapping(true);
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links/${editMappingLink.id}/field-mapping`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ mappings: editMappings }),
      });
      if (res.ok) {
        toast.success('Field mapping saved');
        setEditMappingLink(null);
      } else {
        toast.error('Failed to save mapping');
      }
    } catch {
      toast.error('Network error');
    }
    setSavingMapping(false);
  };

  const filteredAccounts = accounts.filter(a =>
    !accountSearch || a.name.toLowerCase().includes(accountSearch.toLowerCase()) || a.id.includes(accountSearch)
  );

  const filteredPages = pages.filter(p =>
    !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase())
  );

  const filteredClients = clients.filter(c =>
    !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.company_name || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredForms = forms.filter(f =>
    !formSearch || f.name.toLowerCase().includes(formSearch.toLowerCase())
  );

  const stepLabels = ['Client', 'Account', 'Page', 'Form', 'Map Fields'];
  const canGoBack = step > 1;

  return (
    <div className="space-y-8">
      {/* ── Wizard ── */}
      <div className="bg-[#09090B] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#6366f1]/10 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-[#6366f1]" />
          </div>
          <div>
            <h2 className="font-bold text-white text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Link a Meta Lead Gen Form</h2>
            <p className="text-xs text-zinc-500">Connect a Facebook Lead Gen form to a client's CRM for automatic lead import</p>
          </div>
        </div>

        <StepIndicator current={step} total={5} labels={stepLabels} />

        {/* Back button */}
        {canGoBack && (
          <button
            onClick={() => setStep(step - 1)}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />Back
          </button>
        )}

        {/* ── Step 1: Choose Client ── */}
        {step === 1 && (
          <div>
            <p className="text-sm font-semibold text-zinc-300 mb-3">Select a client with CRM enabled</p>
            {/* Client search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                placeholder="Search clients..."
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
              />
            </div>
            {loadingClients ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}</div>
            ) : clients.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No clients with CRM enabled</p>
                <p className="text-zinc-600 text-xs mt-1">Go to Portal Access Control to enable CRM for a client first.</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No clients match your search</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredClients.map(client => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                    className="w-full flex items-center gap-3 p-3.5 bg-black/20 border border-white/5 rounded-xl hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#22c55e]/10 flex items-center justify-center font-bold text-[#22c55e] text-sm flex-shrink-0">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white text-sm">{client.name}</p>
                      {client.company_name && <p className="text-xs text-zinc-500 truncate">{client.company_name}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#6366f1] flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Choose Account ── */}
        {step === 2 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#22c55e]/10 flex items-center justify-center font-bold text-[#22c55e] text-xs flex-shrink-0">
                {selectedClient?.name.charAt(0)}
              </div>
              <p className="text-sm font-semibold text-zinc-300">Choose a Meta Ad Account for <span className="text-white">{selectedClient?.name}</span></p>
            </div>

            {/* Account search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                value={accountSearch}
                onChange={e => setAccountSearch(e.target.value)}
                placeholder="Search accounts..."
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
              />
            </div>

            {loadingAccounts ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <Hash className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">{accounts.length === 0 ? 'No Meta accounts found' : 'No accounts match your search'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {filteredAccounts.map(acc => (
                  <button
                    key={acc.id}
                    onClick={() => handleSelectAccount(acc)}
                    className="flex items-start gap-3 p-3.5 bg-black/20 border border-white/5 rounded-xl hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 transition-colors text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Hash className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{acc.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-zinc-600 font-mono">{acc.id}</span>
                        <span className={`text-[10px] font-bold ${acc.is_active ? 'text-[#22c55e]' : 'text-zinc-500'}`}>
                          {acc.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                        {acc.currency && <span className="text-[10px] text-zinc-600">{acc.currency}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#6366f1] flex-shrink-0 mt-1" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Choose Page ── */}
        {step === 3 && (
          <div>
            <p className="text-sm font-semibold text-zinc-300 mb-3">
              Choose a Facebook Page <span className="text-zinc-500 font-normal">— lead forms are attached to pages</span>
            </p>

            {/* Page search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                value={pageSearch}
                onChange={e => setPageSearch(e.target.value)}
                placeholder="Search pages..."
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
              />
            </div>

            {loadingPages ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}</div>
            ) : pages.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <Globe className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No pages found for this account</p>
                <p className="text-zinc-600 text-xs mt-1 px-4">The token may not have access to any pages. Ensure you're using a User token with page admin access.</p>
              </div>
            ) : filteredPages.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <Globe className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No pages match your search</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPages.map(page => (
                  <button
                    key={page.id}
                    onClick={() => handleSelectPage(page)}
                    className="w-full flex items-center gap-3 p-3.5 bg-black/20 border border-white/5 rounded-xl hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#6366f1]/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-[#6366f1]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white text-sm">{page.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {page.category && <span className="text-[10px] text-zinc-500">{page.category}</span>}
                        {page.fan_count > 0 && (
                          <span className="text-[10px] text-zinc-600">{page.fan_count.toLocaleString('en-IN')} followers</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#6366f1] flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Choose Form + Preview + Confirm ── */}
        {step === 4 && (
          <div>
            <p className="text-sm font-semibold text-zinc-300 mb-3">
              Select a Lead Gen Form from <span className="text-white">{selectedPage?.name}</span>
            </p>

            {/* Form search */}
            {forms.length > 0 && (
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input
                  value={formSearch}
                  onChange={e => setFormSearch(e.target.value)}
                  placeholder="Search forms..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
                />
              </div>
            )}

            {loadingForms ? (
              <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-16" />)}</div>
            ) : formsError ? (
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300">{formsError}</p>
              </div>
            ) : forms.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No lead gen forms found on this page</p>
                <p className="text-zinc-600 text-xs mt-1">Create a Lead Generation form in Meta Ads Manager first.</p>
              </div>
            ) : filteredForms.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
                <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No forms match your search</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredForms.map(form => {
                  const isSelected = selectedForm?.id === form.id;
                  return (
                    <button
                      key={form.id}
                      onClick={() => handleSelectForm(form)}
                      className={`w-full flex items-start gap-3 p-3.5 border rounded-xl transition-colors text-left ${
                        isSelected
                          ? 'border-[#6366f1]/60 bg-[#6366f1]/10'
                          : 'border-white/5 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'bg-[#6366f1]/20' : 'bg-white/5'
                      }`}>
                        {isSelected
                          ? <CheckCircle2 className="w-4 h-4 text-[#6366f1]" />
                          : <FileText className="w-4 h-4 text-zinc-500" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-zinc-200'}`}>{form.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            form.status === 'ACTIVE'
                              ? 'bg-[#22c55e]/20 text-[#22c55e]'
                              : 'bg-zinc-700/40 text-zinc-500'
                          }`}>{form.status}</span>
                          <span className="text-[10px] text-zinc-500">{form.leads_count} leads collected</span>
                          {form.created_time && (
                            <span className="text-[10px] text-zinc-600">{fmtDate(form.created_time)}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Preview */}
            {selectedForm && (
              <div className="mt-5 pt-5 border-t border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-zinc-400" />
                  <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Recent Leads Preview</p>
                </div>
                <LeadsPreviewTable leads={previewLeads} loading={loadingPreview} error={previewError} />
              </div>
            )}

            {/* Continue to field mapping */}
            {selectedForm && (
              <div className="mt-5 bg-[#6366f1]/5 border border-[#6366f1]/20 rounded-xl p-4">
                <p className="text-xs font-semibold text-[#6366f1] uppercase tracking-wider mb-3">Ready to Map Fields</p>
                <div className="space-y-1.5 text-sm mb-4">
                  {[
                    { label: 'Client', value: selectedClient?.name },
                    { label: 'Account', value: selectedAccount?.name },
                    { label: 'Page', value: selectedPage?.name },
                    { label: 'Form', value: selectedForm?.name },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="text-zinc-500 w-16 flex-shrink-0">{label}</span>
                      <span className="text-white font-medium truncate">{value}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleContinueToMapping}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30 text-sm font-bold hover:bg-[#6366f1]/30 transition-colors"
                >
                  <ArrowRight className="w-4 h-4" />Continue to Field Mapping →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Step 5: Field Mapping + Confirm ── */}
        {step === 5 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#6366f1]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-[#6366f1]" />
              </div>
              <p className="text-sm font-semibold text-zinc-300">
                Map fields for <span className="text-white">{selectedForm?.name}</span>
              </p>
            </div>

            <FieldMappingStep
              metaFields={wizardMetaFields}
              mappings={wizardMappings}
              onChange={(field, val) => setWizardMappings(prev => ({ ...prev, [field]: val }))}
              loading={loadingFieldsWizard}
              error={fieldsWizardError}
            />

            <div className="mt-5 pt-5 border-t border-white/5">
              <button
                onClick={handleLinkForm}
                disabled={linking}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 disabled:opacity-50 transition-colors"
              >
                {linking
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Linking…</>
                  : <><Link2 className="w-4 h-4" />Link Form to CRM</>
                }
              </button>
              <p className="text-[10px] text-zinc-600 text-center mt-2">Field mapping will be saved with the link</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Active Links ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#22c55e]/10 flex items-center justify-center">
              <ExternalLink className="w-4 h-4 text-[#22c55e]" />
            </div>
            <div>
              <h2 className="font-bold text-white text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Active Form Links</h2>
              <p className="text-xs text-zinc-500">{links.length} connection{links.length !== 1 ? 's' : ''} configured</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {links.length > 0 && (
              <button
                onClick={handleSyncAll}
                disabled={syncingAll}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/20 text-[#22c55e] text-xs font-semibold hover:bg-[#22c55e]/20 transition-colors disabled:opacity-40"
              >
                {syncingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Sync All
              </button>
            )}
            <button
              onClick={fetchLinks}
              disabled={loadingLinks}
              className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loadingLinks ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <ActiveLinksTable
          links={links}
          loading={loadingLinks}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onSync={handleSync}
          onEditMapping={handleEditMapping}
          toggling={toggling}
          deleting={deleting}
          syncing={syncing}
        />
      </div>

      {/* ── Edit Mapping Modal ── */}
      {editMappingLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#09090B] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-shrink-0">
              <div>
                <h3 className="font-bold text-white text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Field Mapping</h3>
                <p className="text-xs text-zinc-500 mt-0.5 truncate max-w-[300px]">{editMappingLink.form_name || editMappingLink.form_id}</p>
              </div>
              <button
                onClick={() => setEditMappingLink(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <FieldMappingStep
                metaFields={editMappingFields}
                mappings={editMappings}
                onChange={(field, val) => setEditMappings(prev => ({ ...prev, [field]: val }))}
                loading={loadingEditFields}
                error={editFieldsError}
              />
            </div>
            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/5 flex-shrink-0">
              <button
                onClick={handleSaveEditMapping}
                disabled={savingMapping}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 disabled:opacity-50 transition-colors"
              >
                {savingMapping
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>
                  : <><Check className="w-4 h-4" />Save Mapping</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
